import { StationInfo } from '../services/stationService';

export interface RegionBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Filter stations based on geographic bounds
 */
export function filterStationsByRegion(
  stations: StationInfo[],
  bounds: RegionBounds | null,
): StationInfo[] {
  if (!bounds) {
    return stations;
  }

  return stations.filter((station) => {
    const { latitude, longitude } = station;

    // Check if station is within the bounding box
    const isWithinLatitude = latitude >= bounds.south && latitude <= bounds.north;
    const isWithinLongitude = longitude >= bounds.west && longitude <= bounds.east;

    return isWithinLatitude && isWithinLongitude;
  });
}

/**
 * Calculate the center point of a region
 */
export function getRegionCenter(bounds: RegionBounds): [number, number] {
  const centerLng = (bounds.east + bounds.west) / 2;
  const centerLat = (bounds.north + bounds.south) / 2;
  return [centerLng, centerLat];
}

/**
 * Calculate the approximate area of a region in square kilometers
 */
export function calculateRegionArea(bounds: RegionBounds): number {
  const earthRadius = 6371; // km

  // Convert degrees to radians
  const lat1Rad = (bounds.south * Math.PI) / 180;
  const lat2Rad = (bounds.north * Math.PI) / 180;
  const lngDiffRad = ((bounds.east - bounds.west) * Math.PI) / 180;

  // Calculate area using spherical geometry approximation
  const area =
    earthRadius *
    earthRadius *
    Math.abs(lngDiffRad) *
    Math.abs(Math.sin(lat2Rad) - Math.sin(lat1Rad));

  return area;
}

/**
 * Get statistics about stations in a region
 */
export function getStationStats(stations: StationInfo[]): {
  count: number;
  averageElevation: number;
  minElevation: number;
  maxElevation: number;
  elevationRange: number;
} {
  if (stations.length === 0) {
    return {
      count: 0,
      averageElevation: 0,
      minElevation: 0,
      maxElevation: 0,
      elevationRange: 0,
    };
  }

  const elevations = stations.map((s) => s.elevation);
  const minElevation = Math.min(...elevations);
  const maxElevation = Math.max(...elevations);
  const averageElevation = elevations.reduce((sum, elev) => sum + elev, 0) / elevations.length;

  return {
    count: stations.length,
    averageElevation: Math.round(averageElevation),
    minElevation: Math.round(minElevation),
    maxElevation: Math.round(maxElevation),
    elevationRange: Math.round(maxElevation - minElevation),
  };
}
